/**
 * AccountTenantCheckCard — Admin Diagnostics (2026-02).
 *
 * READ-ONLY runtime tenant integrity check. Hits
 * `GET /api/admin/diagnostics/account-tenant-check` and displays:
 *
 *   1. MY ACCOUNT         — id, email, company_id, resolved company name
 *   2. DOCUMENT TEMPLATES — count grouped by company_id (+ resolved name)
 *   3. EMAIL TEMPLATES    — same grouping
 *   4. KEYSTONE COMPANIES — every row whose name/slug matches /keystone/i
 *   5. VERDICT            — plain-English MATCH / DO NOT MATCH
 *   6. MY AVATAR          — stored value + authed probe status code
 *
 * Deliberately runs against whatever `/api/...` the browser can reach,
 * so opening the card in production hits production Mongo.
 *
 * No writes. Super-admin only (backend 404s non-super-admins).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, ShieldQuestion, Copy, RefreshCw, CheckCircle2, XCircle,
  AlertTriangle, Image as ImageIcon,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

function authHeaders() {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function Row({ label, value, mono = true, testId }) {
  const isReactNode = React.isValidElement(value);
  const isEmpty = value === null || value === undefined || value === '';
  return (
    <div className="flex items-baseline gap-3 py-1" data-testid={testId}>
      <div className="text-[11px] text-slate-500 min-w-[160px] font-semibold uppercase tracking-wide">{label}</div>
      <div className={`text-sm text-slate-900 break-all ${mono ? 'font-mono' : ''}`}>
        {isEmpty ? (
          <span className="italic text-slate-400">(empty)</span>
        ) : isReactNode ? value : String(value)}
      </div>
    </div>
  );
}

function CompanyGroupTable({ rows, testId }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="text-xs text-slate-500 italic px-2 py-1" data-testid={`${testId}-empty`}>
        (no rows)
      </div>
    );
  }
  return (
    <table className="w-full text-xs border rounded overflow-hidden" data-testid={testId}>
      <thead className="bg-slate-100">
        <tr className="text-left">
          <th className="p-2">company_id</th>
          <th className="p-2">Resolved name</th>
          <th className="p-2 text-right">Count</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.company_id || 'null'}-${i}`} className="border-t hover:bg-slate-50" data-testid={`${testId}-row-${i}`}>
            <td className="p-2 font-mono text-slate-800">
              {r.company_id ? r.company_id : <span className="italic text-slate-400">null (orphaned)</span>}
            </td>
            <td className="p-2">
              {r.company_name ? (
                <span className="text-slate-900">{r.company_name}</span>
              ) : (
                <span className="italic text-rose-700">NO MATCHING COMPANY</span>
              )}
            </td>
            <td className="p-2 text-right font-mono font-semibold">{r.count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function AccountTenantCheckCard({ isSuperAdmin }) {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/diagnostics/account-tenant-check`,
        { headers: authHeaders() },
      );
      setData(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Request failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  const copyJSON = () => {
    if (!data) return;
    try {
      navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast.success('Copied JSON');
    } catch { /* ignore */ }
  };

  if (!isSuperAdmin) return null;

  const verdict = data?.verdict;
  const my = data?.my_account;
  const avatar = data?.avatar;
  const docsMatch = !!verdict?.doc_templates_match;
  const emailsMatch = !!verdict?.email_templates_match;

  return (
    <div
      className="p-5 rounded-xl border border-slate-300 bg-white mb-6"
      data-testid="account-tenant-check-card"
    >
      <div className="flex items-start gap-3 mb-3">
        <ShieldQuestion className="w-5 h-5 text-slate-700 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-semibold">Account &amp; Tenant Check</h3>
          <p className="text-sm text-slate-700">
            Read-only. Runs against whatever database this pod is bound to right now, so opening
            this card in production reports on production data. Answers the question:
            <em> is my account on the same company as my document templates?</em>
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={run}
            disabled={busy}
            data-testid="account-tenant-check-run-btn"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Running…</>
            ) : data ? (
              <><RefreshCw className="w-4 h-4 mr-2" /> Re-run</>
            ) : (
              <><ShieldQuestion className="w-4 h-4 mr-2" /> Run Account &amp; Tenant Check</>
            )}
          </Button>
          {data && (
            <Button
              variant="ghost"
              size="sm"
              onClick={copyJSON}
              data-testid="account-tenant-check-copy-btn"
            >
              <Copy className="w-4 h-4 mr-1" /> Copy JSON
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 rounded bg-rose-50 border border-rose-200 text-rose-800 text-sm flex items-start gap-2" data-testid="account-tenant-check-error">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>
            <div className="font-semibold">Check failed</div>
            <div className="text-xs">{error}</div>
          </div>
        </div>
      )}

      {data && (
        <div className="space-y-5">
          {/* 5. VERDICT — top of card so the answer is unmissable */}
          <div
            className={`p-4 rounded-lg border ${
              docsMatch && emailsMatch
                ? 'bg-emerald-50 border-emerald-300'
                : 'bg-rose-50 border-rose-300'
            }`}
            data-testid="account-tenant-check-verdict"
          >
            <div className="flex items-center gap-2 mb-1">
              {docsMatch && emailsMatch ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-700" />
              ) : (
                <XCircle className="w-5 h-5 text-rose-700" />
              )}
              <span className={`font-semibold text-sm ${docsMatch && emailsMatch ? 'text-emerald-900' : 'text-rose-900'}`}>
                Verdict
              </span>
              <Badge className={docsMatch ? 'bg-emerald-100 text-emerald-900' : 'bg-rose-100 text-rose-900'} data-testid="account-tenant-check-verdict-doc-badge">
                Document templates: {docsMatch ? 'MATCH' : 'DO NOT MATCH'}
              </Badge>
              <Badge className={emailsMatch ? 'bg-emerald-100 text-emerald-900' : 'bg-rose-100 text-rose-900'} data-testid="account-tenant-check-verdict-email-badge">
                Email templates: {emailsMatch ? 'MATCH' : 'DO NOT MATCH'}
              </Badge>
            </div>
            <div className="text-sm text-slate-900" data-testid="account-tenant-check-verdict-sentence">
              {verdict.sentence}
            </div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <div className="p-2 rounded bg-white border">
                <div className="text-[10px] uppercase text-slate-500">Your company_id</div>
                <div className="font-mono break-all">{verdict.my_company_id || <em className="text-slate-400">(none)</em>}</div>
              </div>
              <div className="p-2 rounded bg-white border">
                <div className="text-[10px] uppercase text-slate-500">Doc-template owner</div>
                <div className="font-mono break-all">{verdict.doc_owner_company_id || <em className="text-slate-400">(none)</em>}</div>
              </div>
              <div className="p-2 rounded bg-white border">
                <div className="text-[10px] uppercase text-slate-500">Email-template owner</div>
                <div className="font-mono break-all">{verdict.email_owner_company_id || <em className="text-slate-400">(none)</em>}</div>
              </div>
            </div>
          </div>

          {/* 1. MY ACCOUNT */}
          <section data-testid="account-tenant-check-my-account">
            <h4 className="text-sm font-semibold text-slate-800 mb-1">1. My account</h4>
            <div className="rounded border bg-slate-50 px-3 py-2">
              <Row label="user id" value={my.user_id} testId="mac-user-id" />
              <Row label="email" value={my.email} testId="mac-email" />
              <Row label="my company_id" value={my.my_company_id} testId="mac-company-id" />
              <Row
                label="my company name"
                mono={false}
                value={my.my_company_row_found ? my.my_company_name : 'NO MATCHING COMPANY'}
                testId="mac-company-name"
              />
              <Row label="is_active" value={String(my.is_active)} testId="mac-is-active" />
              <Row label="is_super_admin" value={String(my.is_super_admin)} testId="mac-is-super-admin" />
              <Row label="role" value={my.role} testId="mac-role" />
            </div>
          </section>

          {/* 2. DOCUMENT TEMPLATES */}
          <section data-testid="account-tenant-check-doc-templates">
            <h4 className="text-sm font-semibold text-slate-800 mb-1">2. Document templates — count by company_id</h4>
            <CompanyGroupTable rows={data.document_templates} testId="account-tenant-check-doc-table" />
          </section>

          {/* 3. EMAIL TEMPLATES */}
          <section data-testid="account-tenant-check-email-templates">
            <h4 className="text-sm font-semibold text-slate-800 mb-1">3. Email templates — count by company_id</h4>
            <CompanyGroupTable rows={data.email_templates} testId="account-tenant-check-email-table" />
          </section>

          {/* 4. KEYSTONE COMPANIES */}
          <section data-testid="account-tenant-check-keystone">
            <h4 className="text-sm font-semibold text-slate-800 mb-1">
              4. Keystone-matching companies <span className="text-xs text-slate-500">(name/slug regex, case-insensitive)</span>
            </h4>
            {data.keystone_companies.length === 0 ? (
              <div className="text-xs text-slate-500 italic px-2 py-1">(no company row with &quot;keystone&quot; in name or slug)</div>
            ) : (
              <table className="w-full text-xs border rounded overflow-hidden" data-testid="account-tenant-check-keystone-table">
                <thead className="bg-slate-100">
                  <tr className="text-left">
                    <th className="p-2">id</th>
                    <th className="p-2">name</th>
                    <th className="p-2">slug</th>
                  </tr>
                </thead>
                <tbody>
                  {data.keystone_companies.map((c, i) => (
                    <tr key={c.id} className="border-t" data-testid={`account-tenant-check-keystone-row-${i}`}>
                      <td className="p-2 font-mono break-all">{c.id}</td>
                      <td className="p-2">{c.name}</td>
                      <td className="p-2 font-mono">{c.slug}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* 6. MY AVATAR */}
          <section data-testid="account-tenant-check-avatar">
            <h4 className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-1">
              <ImageIcon className="w-4 h-4" /> 6. My avatar <span className="text-xs text-slate-500 font-normal">(read-only probe)</span>
            </h4>
            <div className="rounded border bg-slate-50 px-3 py-2">
              <Row label="users.avatar" value={avatar.stored_value} testId="avatar-stored-value" />
              <Row label="probed URL" value={avatar.absolute_url} testId="avatar-absolute-url" />
              <Row
                label="HTTP status"
                mono={false}
                value={
                  avatar.http_status === null || avatar.http_status === undefined
                    ? (avatar.error ? 'request failed (see error below)' : '—')
                    : (
                      <span className={`${avatar.http_status === 200 ? 'text-emerald-800' : 'text-rose-800'} font-mono font-semibold`}>
                        {avatar.http_status}
                        {avatar.http_status === 200 ? ' — image bytes returned' : ' — fetch failed'}
                      </span>
                    )
                }
                testId="avatar-http-status"
              />
              <Row label="content-type" value={avatar.content_type} testId="avatar-content-type" />
              <Row label="byte length" value={avatar.byte_length !== null && avatar.byte_length !== undefined ? String(avatar.byte_length) : null} testId="avatar-byte-length" />
              {avatar.error && (
                <Row label="probe error" value={avatar.error} testId="avatar-error" />
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
